import React, { useMemo } from 'react';
import { useLocalStorage } from 'react-use';
import { keyBy, isFunction, fromPairs, sortBy } from 'lodash';
import { collection, query, } from 'firebase/firestore';
import { eachMonthOfInterval, addMonths, endOfMonth, format as formatDate, startOfMonth } from 'date-fns';
import classnames from 'classnames';
import numeral from 'numeral';

import useQueryParams from '../hooks/useQueryParams';
import useTenant from '../hooks/useTenant';
import TenantPage from '../TenantPage';
import QuerySelector from '../QuerySelector';
import QueryYearSelector from '../QueryYearSelector';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import PlTabs from '../PlTabs';
import NumberDiscrepancyHelpButton from '../NumberDiscrepancyHelpButton';
import ExportButton from '../ExportButton';
import QueryBoolean from '../QueryBoolean';
import MallBadge from '../MallBadge';
import { mapKeysToJa, jas } from '../../shared/texts';
import usePlCalculation from '../hooks/usePlCalculation';

export default function MonthlyPL (props) {
  return (
    <TenantPage>
      <PageContent {...props} />
    </TenantPage>
  );
};

function PageContent (props) {
  const { tenant } = props;
  const queryParams = useQueryParams();
  const fiscalYearEnd = tenant.fiscalYearEnd || 12;
  const currentYear = new Date().getFullYear();
  const selectedYear = queryParams.year ? parseInt(queryParams.year) : currentYear;

  const sellers = useCollectionSubscription(query(collection(tenant.ref, 'sellers')));
  const shops = useCollectionSubscription(query(collection(tenant.ref, 'shops')));
  const isOwnerSelected = !!queryParams.sellerId;
  const selectedSellers = useMemo(_ => isOwnerSelected ? sellers.filter(_ => _.id === queryParams.sellerId) : sellers, [queryParams.sellerId, sellers]);
  const selectedShops = useMemo(_ => isOwnerSelected ? shops.filter(_ => _.id === queryParams.sellerId) : shops, [queryParams.sellerId, shops]);
  const sellerOptions = sortBy([
    ...sellers.map(_ => ({ label: _.name, value: _.id, index: _.index })),
    ...shops.map(_ => ({ label: _.name, value: _.id, index: _.index })),
  ], _ => _.index ?? Infinity);
  // テナント設定「税抜き表示のデフォルト」をデフォルトに。URLクエリ指定があればそちら優先
  const defaultExcludeTax = tenant.exportExcludeTax === 'excluded';
  const isExcludeTax = queryParams.excludeTax != null ? queryParams.excludeTax === '1' : defaultExcludeTax;

  // 折りたたみ状態の管理
  const [collapsed = {}, setCollapsed] = useLocalStorage('monthlyPlCollapsed', {});

  const toggleCollapse = (key) => {
    setCollapsed({ ...collapsed, [key]: !collapsed[key] });
  };

  // 会計年度の12ヶ月を生成
  const fiscalMonths = useMemo(() => {
    const endDate = endOfMonth(new Date(selectedYear, fiscalYearEnd - 1, 1));
    const startDate = addMonths(endDate, -11);
    
    return eachMonthOfInterval({ start: startDate, end: endDate });
  }, [selectedYear, fiscalYearEnd]);

  // 各月の期間を生成
  const dateRanges = useMemo(() => 
    fiscalMonths.map(_ => [startOfMonth(_), endOfMonth(_)])
  , [fiscalMonths]);
  
  // 複数期間のPLデータを取得
  const { periodData, items: totalItems } = usePlCalculation({
    dateRanges,
    sellers: selectedSellers,
    shops: selectedShops,
    dateRangeType: 'monthly',
    isExcludeTax,
  });


  // 各月のデータを生成
  const monthlyData = useMemo(() => 
    fiscalMonths.map((_, index) => {
      const period = periodData?.[index];
      return {
        date: _,
        year: _.getFullYear(),
        month: _.getMonth() + 1,
        startDate: startOfMonth(_),
        endDate: endOfMonth(_),
        items: period?.items || {},
        salesAmount: period?.salesAmount || 0,
        periodData: period
      };
    })
  , [fiscalMonths, periodData]);

  // 年度合計を計算
  const yearTotalSalesAmount = monthlyData.reduce((x, data) => x + data.salesAmount, 0);

  // エクスポート用のデータ生成関数
  const getExportData = () => {
    return itemStructure.flatMap(itemConfig => {
      const { key, hasChildren, children } = itemConfig;
      
      // 親項目
      const parentRow = mapKeysToJa({ 
        itemName: jas[key] || key, 
        ...fromPairs(monthlyData.map(_ => [
          formatDate(_.date, 'yyyy/MM'),
          _.items[key]?.computeValue() || 0
        ])),
        yearTotal: monthlyData.reduce((x, _) => x + (_.items[key]?.computeValue() || 0), 0)
      });
      
      // 子項目
      const childRows = hasChildren && children ? children.map(childKey => 
        mapKeysToJa({ 
          itemName: `  ${jas[childKey] || childKey}`, 
          ...fromPairs(monthlyData.map(_ => [
            formatDate(_.date, 'yyyy/MM'),
            _.items[key]?.children?.[childKey]?.computeValue() || 0
          ])),
          yearTotal: monthlyData.reduce((x, _) => x + (_.items[key]?.children?.[childKey]?.computeValue() || 0), 0)
        })
      ) : [];
      
      return [parentRow, ...childRows];
    });
  };

  // 階層構造を持つ項目定義
  const firstMonthItems = monthlyData[0]?.items || {};
  const itemStructure = useMemo(() => [
    { key: 'sales', hasChildren: false },
    { key: 'refund', hasChildren: false },
    {
      key: 'cods',
      hasChildren: true,
      children: Object.keys(firstMonthItems.cods?.children || {})
    },
    { key: 'grossProfit', hasChildren: false },
    { 
      key: 'cods2',
      hasChildren: true,
      children: Object.keys(firstMonthItems.cods2?.children || {})
    },
    { key: 'grossProfit2', hasChildren: false },
    { 
      key: 'variableSga',
      hasChildren: true,
      children: Object.keys(firstMonthItems.variableSga?.children || {})
    },
    { key: 'contributionMargin', hasChildren: false },
    { 
      key: 'fixedSga',
      hasChildren: true,
      children: Object.keys(firstMonthItems.fixedSga?.children || {})
    },
    { key: 'operatingProfit', hasChildren: false }
  ], [firstMonthItems]);

  // 行をレンダリングする関数
  const renderRow = (itemConfig, isChild = false, parentKey = null) => {
    const { key, hasChildren, children } = itemConfig;
    
    const item = isChild && parentKey ? 
      firstMonthItems[parentKey]?.children?.[key] : 
      firstMonthItems[key];
    
    if (!item) return null;
    
    const { isComputed, label } = item;
    const isCollapsed = collapsed[key];
    
    const parentRow = (
      <tr key={key} className={classnames(
        { 'table-light': !isChild },
        { 'bg-white': isChild }
      )}>
        <td className={classnames({ 'fw-bold': isComputed })} style={{ verticalAlign: 'middle' }}>
          <div className="d-flex align-items-center">
            {isChild && <span className="me-2">&nbsp;&nbsp;</span>}
            {hasChildren && (
              <button
                className="btn p-0 me-2 border-0 bg-transparent text-muted"
                onClick={() => toggleCollapse(key)}
                style={{ 
                  fontSize: '10px', 
                  lineHeight: 1,
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title={isCollapsed ? '展開' : '折りたたみ'}
              >
                {isCollapsed ? '▶' : '▼'}
              </button>
            )}
            <span>{isFunction(label) ? label() : label}</span>
            <MallBadge feeKey={key} />
          </div>
        </td>
        {monthlyData.map((monthData, index) => {
          const itemToUse = isChild && parentKey ? 
            monthData.items[parentKey]?.children?.[key] : 
            monthData.items[key];
          const value = itemToUse?.computeValue() || 0;
          const rate = monthData.salesAmount > 0 ? Math.abs(value) / monthData.salesAmount : 0;
          
          return (
            <td key={formatDate(monthData.date, 'yyyy-MM')} className="text-end" style={{ verticalAlign: 'middle' }}>
              <div className={classnames('fw-semibold', { 'text-danger': value < 0 })} style={{ lineHeight: 1.2 }}>
                {numeral(value).format()}円
              </div>
              <div className="text-muted small" style={{ lineHeight: 1.1, marginTop: '2px' }}>
                {monthData.salesAmount > 0 ? numeral(rate).format('0.0%') : '-'}
              </div>
            </td>
          );
        })}
        <td className="text-end" style={{ verticalAlign: 'middle' }}>
          {(() => {
            const totalValue = monthlyData.reduce((x, monthData) => {
              const itemToUse = isChild && parentKey ? 
                monthData.items[parentKey]?.children?.[key] : 
                monthData.items[key];
              return x + (itemToUse?.computeValue() || 0);
            }, 0);
            const totalRate = yearTotalSalesAmount > 0 ? Math.abs(totalValue) / yearTotalSalesAmount : 0;
            
            return (
              <>
                <div className={classnames('fw-bold', { 'text-danger': totalValue < 0 })} style={{ lineHeight: 1.2 }}>
                  {numeral(totalValue).format()}円
                </div>
                <div className="text-muted small" style={{ lineHeight: 1.1, marginTop: '2px' }}>
                  {yearTotalSalesAmount > 0 ? numeral(totalRate).format('0.0%') : '-'}
                </div>
              </>
            );
          })()} 
        </td>
      </tr>
    );
    
    if (hasChildren && !isCollapsed && children && children.length > 0) {
      const childRows = children.flatMap(childKey => 
        renderRow({ key: childKey, hasChildren: false }, true, key)
      );
      return [parentRow, ...childRows];
    }
    
    return [parentRow];
  };

  return (
    <div className="monthly-pl container-fluid">
      <div className="px-4">
        <div className="row">
          <div className="col-12">
            <div className="card p-4">
              <PlTabs activeTab="monthly" />
              <div className="mb-4 d-flex gap-2 align-items-end justify-content-start">
                <QueryYearSelector paramName="year" defaultValue={currentYear} />
                {sellerOptions.length >= 2 && <QuerySelector label="ショップ" paramName="sellerId" options={sellerOptions} style={{ width: 180 }} />}
                <QueryBoolean paramName="excludeTax" label="税抜き" defaultValue={defaultExcludeTax ? '1' : '0'} />
                <ExportButton
                  label={false}
                  outline
                  fileName={`月次PL_${selectedYear}年度.csv`}
                  rows={getExportData}
                />
                <div className="ms-auto">
                  <NumberDiscrepancyHelpButton sellers={sellers} shops={shops} />
                </div>
              </div>
              <div className="container-fluid px-0">
                <div className="table-responsive" style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                  <table className="table mb-0">
                    <thead>
                      <tr>
                        <th style={{ minWidth: '140px', width: '140px' }} className="fw-bold sticky-col"></th>
                        {fiscalMonths.map(_ => (
                          <th key={formatDate(_, 'yyyy-MM')} className="text-center fw-bold" style={{ minWidth: '100px', width: '100px' }}>
                            <div>{formatDate(_, 'yyyy/MM')}</div>
                          </th>
                        ))}
                        <th className="text-center fw-bold" style={{ minWidth: '110px', width: '110px' }}>
                          <div>年度合計</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemStructure.flatMap(item => renderRow(item))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
